import { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { useSiteContent } from "@/context/SiteContentContext";

const Register = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { getContent } = useSiteContent();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: "",
    role: "user",
    languages: "",
    specializations: "",
    court_certifications: "",
    business_name: "",
    business_address: "",
    business_phone: "",
  });
  const [loading, setLoading] = useState(false);

  // Use XMLHttpRequest to avoid body stream issues from fetch interceptors
  const apiRequest = (url, options) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      
      xhr.timeout = 10000;
      
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          resolve({ ok: false, status: xhr.status, data: { detail: 'Invalid server response' } });
        }
      };
      
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      
      xhr.send(options.body || null);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      email: formData.email,
      password: formData.password,
      full_name: formData.full_name,
      phone: formData.phone || "",
      role: formData.role,
      languages: formData.languages ? formData.languages.split(",").map(l => l.trim()).filter(l => l) : [],
      specializations: formData.specializations ? formData.specializations.split(",").map(s => s.trim()).filter(s => s) : [],
      court_certifications: formData.court_certifications ? formData.court_certifications.split(",").map(c => c.trim()).filter(c => c) : [],
      business_name: formData.business_name || "",
      business_address: formData.business_address || "",
      business_phone: formData.business_phone || "",
    };

    try {
      const { ok, data } = await apiRequest(`${API}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (ok) {
        login(data.token, data.user);
        toast.success("Registration successful!");
        
        if (data.user.role === "interpreter") {
          navigate("/interpreter/dashboard");
        } else {
          navigate("/user/dashboard");
        }
      } else {
        toast.error(data.detail || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Globe className="w-10 h-10 text-slate-900" />
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Book an Interpreter</h1>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>{getContent("register", "title", "Create Account")}</h2>
          <p className="text-slate-600 mt-2" style={{ fontFamily: 'Public Sans' }}>{getContent("register", "subtitle", "Join our interpreter network")}</p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="full_name" className="text-slate-700 font-medium">Full Name</Label>
              <Input
                id="full_name"
                data-testid="register-fullname-input"
                type="text"
                required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
              <Input
                id="email"
                data-testid="register-email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <Input
                id="password"
                data-testid="register-password-input"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                placeholder="••••••••"
              />
              <PasswordStrengthMeter password={formData.password} />
            </div>

            <div>
              <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number</Label>
              <Input
                id="phone"
                data-testid="register-phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <Label className="text-slate-700 font-medium mb-3 block">I am a...</Label>
              <RadioGroup
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="user" id="user" data-testid="register-role-user" />
                  <Label htmlFor="user" className="cursor-pointer">User</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="interpreter" id="interpreter" data-testid="register-role-interpreter" />
                  <Label htmlFor="interpreter" className="cursor-pointer">Interpreter</Label>
                </div>
              </RadioGroup>
            </div>

            {formData.role === "interpreter" && (
              <>
                <div>
                  <Label htmlFor="languages" className="text-slate-700 font-medium">Languages (comma-separated)</Label>
                  <Input
                    id="languages"
                    data-testid="register-languages-input"
                    type="text"
                    value={formData.languages}
                    onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                    className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="English, Spanish, French"
                  />
                </div>

                <div>
                  <Label htmlFor="specializations" className="text-slate-700 font-medium">Specializations (comma-separated)</Label>
                  <Input
                    id="specializations"
                    data-testid="register-specializations-input"
                    type="text"
                    value={formData.specializations}
                    onChange={(e) => setFormData({ ...formData, specializations: e.target.value })}
                    className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="Medical, Legal, Business"
                  />
                </div>

                <div>
                  <Label htmlFor="court_certifications" className="text-slate-700 font-medium">Court Certifications (comma-separated, optional)</Label>
                  <Input
                    id="court_certifications"
                    data-testid="register-court-certifications-input"
                    type="text"
                    value={formData.court_certifications}
                    onChange={(e) => setFormData({ ...formData, court_certifications: e.target.value })}
                    className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="Federal Court, State Supreme Court, Immigration Court"
                  />
                </div>
              </>
            )}

            {formData.role === "user" && (
              <>
                <div>
                  <Label htmlFor="business_name" className="text-slate-700 font-medium">Business Name (optional)</Label>
                  <Input
                    id="business_name"
                    data-testid="register-business-name-input"
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <Label htmlFor="business_address" className="text-slate-700 font-medium">Business Address (optional)</Label>
                  <Input
                    id="business_address"
                    data-testid="register-business-address-input"
                    type="text"
                    value={formData.business_address}
                    onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                    className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="123 Main St, City, State, ZIP"
                  />
                </div>

                <div>
                  <Label htmlFor="business_phone" className="text-slate-700 font-medium">Business Phone (optional)</Label>
                  <Input
                    id="business_phone"
                    data-testid="register-business-phone-input"
                    type="tel"
                    value={formData.business_phone}
                    onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                    className="mt-2 h-11 rounded-md border-slate-200 focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </>
            )}

            <Button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-md py-6 font-semibold shadow-sm hover:shadow-md transition-all"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600" style={{ fontFamily: 'Public Sans' }}>
              Already have an account?{" "}
              <Link to="/login" className="text-slate-900 font-semibold hover:underline">
                Log in here
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;